import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, Book, Calendar, Loader2, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Navigation from '../components/Navigation/Navigation';
import apiClient from '../utils/apiClient';
import './LectionaryPage.css';

interface LectionaryReading {
  type: 'OT' | 'Psalm' | 'Epistle' | 'Gospel' | 'NT' | 'Office-Morning' | 'Office-Evening' | 'Office-OT' | 'Office-NT' | 'Other';
  reference: string;
  text?: string;
}

interface LectionaryResult {
  date: string;
  tradition: string;
  traditionName: string;
  readings: LectionaryReading[];
  meta: {
    source: string;
    raw?: any;
  };
}

interface Tradition {
  key: string;
  name: string;
}

interface LectionaryPageProps {
  hideNavigation?: boolean;
}

const LectionaryPage: React.FC<LectionaryPageProps> = ({ hideNavigation = false }) => {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState<string>(
    new Date().toISOString().split('T')[0]
  );
  const [traditions, setTraditions] = useState<Tradition[]>([]);
  const [selectedTraditions, setSelectedTraditions] = useState<string[]>(['ORTHODOX_ORTHOCAL']);
  const [selectedCWOffices, setSelectedCWOffices] = useState<string[]>(['holyCommunion', 'morningPrayer', 'eveningPrayer', 'additionalWeekday']);
  const [lectionaryData, setLectionaryData] = useState<LectionaryResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedTraditions, setExpandedTraditions] = useState<string[]>([]);

  const fetchTraditions = async () => {
    try {
      const response = await apiClient.get('/lectionary/traditions');
      // Filter to only show working traditions (CW, RCL Daily, and Orthodox)
      const workingTraditions = response.data.filter((t: Tradition) =>
        t.key === 'CW' || t.key === 'RCL_DAILY' || t.key === 'ORTHODOX_ORTHOCAL'
      );
      setTraditions(workingTraditions);
    } catch (err) {
      console.error('Failed to fetch traditions:', err);
      setError('Failed to load lectionary traditions');
    }
  };

  const fetchReadings = useCallback(async () => {
    if (selectedTraditions.length === 0) return;

    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.post(`/lectionary/${selectedDate}`, {
        traditions: selectedTraditions
      });

      // Filter CW readings by selected office types
      const filteredData = response.data.map((tradition: LectionaryResult) => {
        if (tradition.tradition === 'CW' && selectedCWOffices.length > 0) {
          const filteredReadings = tradition.readings.filter((reading: LectionaryReading) => {
            // Check if reading belongs to any selected office
            if (reading.reference.includes('[Holy Communion]') && selectedCWOffices.includes('holyCommunion')) return true;
            if (reading.reference.includes('[Morning Prayer]') && selectedCWOffices.includes('morningPrayer')) return true;
            if (reading.reference.includes('[Evening Prayer]') && selectedCWOffices.includes('eveningPrayer')) return true;
            if (reading.reference.includes('[Additional Weekday]') && selectedCWOffices.includes('additionalWeekday')) return true;
            return false;
          });
          return { ...tradition, readings: filteredReadings };
        }
        return tradition;
      });

      setLectionaryData(filteredData);
      // Expand all traditions by default when new data loads
      setExpandedTraditions(filteredData.map((r: LectionaryResult) => r.tradition));
    } catch (err: any) {
      console.error('Failed to fetch readings:', err);
      setError(err.response?.data?.error || 'Failed to load readings');
      setLectionaryData([]);
    } finally {
      setLoading(false);
    }
  }, [selectedDate, selectedTraditions, selectedCWOffices]);

  // Fetch available traditions on mount
  useEffect(() => {
    fetchTraditions();
  }, []);

  // Fetch readings when date or selected traditions change
  useEffect(() => {
    if (selectedTraditions.length > 0) {
      fetchReadings();
    }
  }, [selectedDate, selectedTraditions, fetchReadings]);

  const toggleTradition = (traditionKey: string) => {
    setSelectedTraditions(prev =>
      prev.includes(traditionKey)
        ? prev.filter(t => t !== traditionKey)
        : [...prev, traditionKey]
    );
  };

  const toggleCWOffice = (officeKey: string) => {
    setSelectedCWOffices(prev =>
      prev.includes(officeKey)
        ? prev.filter(o => o !== officeKey)
        : [...prev, officeKey]
    );
  };

  const toggleExpanded = (traditionKey: string) => {
    setExpandedTraditions(prev =>
      prev.includes(traditionKey)
        ? prev.filter(t => t !== traditionKey)
        : [...prev, traditionKey]
    );
  };

  const getReadingTypeIcon = (type: LectionaryReading['type']) => {
    switch (type) {
      case 'Gospel': return '✝️';
      case 'Psalm': return '🎵';
      case 'Epistle': return '✉️';
      case 'OT': return '📜';
      case 'NT': return '📖';
      case 'Office-Morning': return '🌅';
      case 'Office-Evening': return '🌆';
      default: return '📄';
    }
  };

  const getReadingTypeLabel = (type: LectionaryReading['type']) => {
    switch (type) {
      case 'OT': return 'Old Testament';
      case 'NT': return 'New Testament';
      case 'Office-Morning': return 'Morning Office';
      case 'Office-Evening': return 'Evening Office';
      case 'Office-OT': return 'Office - Old Testament';
      case 'Office-NT': return 'Office - New Testament';
      default: return type;
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString + 'T00:00:00');
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <>
      {!hideNavigation && <Navigation />}
      <div className="lectionary-page">
        <div className="lectionary-header">
          <button
            onClick={() => navigate('/settings')}
            className="back-button"
          >
            <ArrowLeft size={20} />
            Back to Settings
          </button>
          <div className="header-content">
            <div className="header-icon">
              <Book size={32} />
            </div>
            <div>
              <h1>Daily Lectionary Readings</h1>
              <p>View readings from various Christian traditions</p>
              <p style={{ fontSize: '0.9rem', color: '#888', marginTop: '0.5rem' }}>
                Note: Currently Orthodox (via Orthocal API) and Church of England Common Worship daily lectionaries are available.
              </p>
            </div>
          </div>
        </div>

        <div className="lectionary-controls">
          <div className="date-selector">
            <Calendar size={20} />
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="date-input"
            />
            <button
              onClick={() => setSelectedDate(new Date().toISOString().split('T')[0])}
              className="today-button"
              title="Go to today"
            >
              Today
            </button>
          </div>

          <div className="tradition-selector">
            <h3>Select Traditions:</h3>
            <div className="tradition-checkboxes">
              {traditions.map(tradition => (
                <div key={tradition.key} className="tradition-option">
                  <label className="tradition-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedTraditions.includes(tradition.key)}
                      onChange={() => toggleTradition(tradition.key)}
                    />
                    <span>{tradition.name}</span>
                  </label>

                  {/* CW Office Type Checkboxes */}
                  {tradition.key === 'CW' && selectedTraditions.includes('CW') && (
                    <div className="cw-office-checkboxes">
                      <label className="office-checkbox">
                        <input
                          type="checkbox"
                          checked={selectedCWOffices.includes('holyCommunion')}
                          onChange={() => toggleCWOffice('holyCommunion')}
                        />
                        <span>Holy Communion</span>
                      </label>
                      <label className="office-checkbox">
                        <input
                          type="checkbox"
                          checked={selectedCWOffices.includes('morningPrayer')}
                          onChange={() => toggleCWOffice('morningPrayer')}
                        />
                        <span>Morning Prayer</span>
                      </label>
                      <label className="office-checkbox">
                        <input
                          type="checkbox"
                          checked={selectedCWOffices.includes('eveningPrayer')}
                          onChange={() => toggleCWOffice('eveningPrayer')}
                        />
                        <span>Evening Prayer</span>
                      </label>
                      <label className="office-checkbox">
                        <input
                          type="checkbox"
                          checked={selectedCWOffices.includes('additionalWeekday')}
                          onChange={() => toggleCWOffice('additionalWeekday')}
                        />
                        <span>Additional Weekday Lectionary</span>
                      </label>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <button
            onClick={fetchReadings}
            disabled={loading || selectedTraditions.length === 0}
            className="refresh-button"
          >
            {loading ? (
              <>
                <Loader2 size={20} className="spinner" />
                Loading...
              </>
            ) : (
              <>
                <RefreshCw size={20} />
                Refresh
              </>
            )}
          </button>
        </div>

        {error && (
          <div className="error-message">
            <p>{error}</p>
          </div>
        )}

        {!loading && lectionaryData.length === 0 && !error && (
          <div className="no-data-message">
            <Book size={48} />
            <p>Select at least one tradition to view readings</p>
          </div>
        )}

        <div className="lectionary-results">
          <div className="date-display">
            <h2>{formatDate(selectedDate)}</h2>
          </div>

          {lectionaryData.map(tradition => (
            <div key={tradition.tradition} className="tradition-card">
              <button
                className="tradition-card-header"
                onClick={() => toggleExpanded(tradition.tradition)}
              >
                <div className="tradition-header-content">
                  <h3>{tradition.traditionName}</h3>
                  <span className="reading-count">
                    {tradition.readings.length} reading{tradition.readings.length !== 1 ? 's' : ''}
                  </span>
                </div>
                {expandedTraditions.includes(tradition.tradition) ? (
                  <ChevronUp size={20} />
                ) : (
                  <ChevronDown size={20} />
                )}
              </button>

              {expandedTraditions.includes(tradition.tradition) && (
                <div className="tradition-readings">
                  {tradition.readings.length === 0 ? (
                    <p className="no-readings">No readings available for this date</p>
                  ) : (
                    tradition.readings.map((reading, index) => (
                      <div key={index} className="reading-item">
                        <div className="reading-header">
                          <span className="reading-icon">{getReadingTypeIcon(reading.type)}</span>
                          <span className="reading-type">{getReadingTypeLabel(reading.type)}</span>
                        </div>
                        <div className="reading-reference">{reading.reference}</div>
                        {reading.text && (
                          <div className="reading-text">
                            <span>{String(reading.text).trim()}</span>
                          </div>
                        )}
                      </div>
                    ))
                  )}
                  <div className="tradition-source">
                    <small>Source: {tradition.meta.source}</small>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default LectionaryPage;
